import { useQuery } from '@tanstack/react-query';
import { adminService } from '@/services/admin.service';
import { ArrowLeft, DollarSign, TrendingDown, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export function AnalyticsCosts() {
  const { data: analytics, isLoading } = useQuery({
    queryKey: ['analytics', '30d'],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('accessToken') || '';
        return await adminService.getCostAnalytics(token);
      } catch (error) {
        console.error('Failed to fetch cost analytics:', error);
        return null;
      }
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return <div>No analytics data available</div>;
  }

  const { llm_costs } = analytics;
  const costSavings = llm_costs.cost_savings;

  // Prepare data for charts
  const modelCostData = llm_costs.by_model.map((item: any) => ({
    name: item.model,
    cost: item.cost,
    requests: item.requests,
  }));

  const operationCostData = llm_costs.by_operation.map((item: any) => ({
    name: item.operation,
    cost: item.cost,
    requests: item.requests,
  }));

  // Monthly cost trends (mock data based on current costs)
  const costTrends = [
    { month: 'Jul', gpt4o: 15.2, gpt4oMini: 5.1, claude: 3.2, total: 23.5 },
    { month: 'Aug', gpt4o: 16.8, gpt4oMini: 5.8, claude: 3.5, total: 26.1 },
    { month: 'Sep', gpt4o: 17.5, gpt4oMini: 6.0, claude: 3.6, total: 27.1 },
    { month: 'Oct', gpt4o: 18.0, gpt4oMini: 6.2, claude: 3.7, total: 27.9 },
    { month: 'Nov', gpt4o: 18.35, gpt4oMini: 6.28, claude: 3.82, total: 28.45 },
  ];

  // Token usage trends
  const tokenTrends = analytics.generation_trend.map((item: any) => ({
    date: item.date,
    input: 0,
    output: 0,
    total: item.count * 450 / 1000,
  }));

  const totalCost = llm_costs.by_model.reduce((sum: number, item: any) => sum + item.cost, 0);
  const totalRequests = llm_costs.by_model.reduce((sum: number, item: any) => sum + item.requests, 0);
  const avgCostPerRequest = totalCost / totalRequests;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/analytics"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">LLM Cost Analysis</h1>
            <p className="text-gray-600">Detailed breakdown of AI model costs and usage</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent">
            <option>Last 30 days</option>
            <option>Last 7 days</option>
            <option>Last 90 days</option>
            <option>All time</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Cost</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                ${totalCost.toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {totalRequests.toLocaleString()} requests
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Cost/Request</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                ${avgCostPerRequest.toFixed(4)}
              </p>
            </div>
            <div className="p-3 bg-fk-orange-vibrant100 rounded-lg">
              <Zap className="w-6 h-6 text-#ea7500" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">Per API call</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Cache Savings</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                ${costSavings.cache_savings.toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingDown className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {((costSavings.cache_savings / costSavings.potential_cost_without_optimization) * 100).toFixed(1)}% reduction
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Savings</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                ${costSavings.total_savings.toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingDown className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Cache + RAG optimization
          </p>
        </div>
      </div>

      {/* Cost Optimization Impact */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-green-900 mb-2">
              Cost Optimization Impact
            </h3>
            <p className="text-green-700 mb-4">
              Your optimizations are saving ${costSavings.total_savings.toFixed(2)} per month
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-green-600 font-medium">Potential Cost</p>
                <p className="text-2xl font-bold text-green-900">
                  ${costSavings.potential_cost_without_optimization.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">Actual Cost</p>
                <p className="text-2xl font-bold text-green-900">
                  ${totalCost.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">Savings</p>
                <p className="text-2xl font-bold text-green-900">
                  {((costSavings.total_savings / costSavings.potential_cost_without_optimization) * 100).toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cost by Model & Operation */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost by Model */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost by Model</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={modelCostData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="cost"
              >
                {modelCostData.map((_entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => `$${value.toFixed(2)}`}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {llm_costs.by_model.map((item: any, index: number) => (
              <div key={item.model} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  ></div>
                  <span className="text-sm text-gray-700">{item.model}</span>
                </div>
                <div className="text-sm font-medium text-gray-900">
                  ${item.cost.toFixed(2)} ({item.requests.toLocaleString()} req)
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cost by Operation */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost by Operation</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={operationCostData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip
                formatter={(value: number) => `$${value.toFixed(2)}`}
              />
              <Bar dataKey="cost" fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {llm_costs.by_operation.map((item: any) => (
              <div key={item.operation} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{item.operation}</span>
                <div className="text-sm font-medium text-gray-900">
                  ${item.cost.toFixed(2)} ({item.percent.toFixed(1)}%)
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cost Trends Over Time */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Trends (Last 5 Months)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart data={costTrends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip
              formatter={(value: number) => `$${value.toFixed(2)}`}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="gpt4o"
              stackId="1"
              stroke="#3B82F6"
              fill="#3B82F6"
              name="GPT-4o"
            />
            <Area
              type="monotone"
              dataKey="gpt4oMini"
              stackId="1"
              stroke="#10B981"
              fill="#10B981"
              name="GPT-4o-mini"
            />
            <Area
              type="monotone"
              dataKey="claude"
              stackId="1"
              stroke="#F59E0B"
              fill="#F59E0B"
              name="Claude 3.5"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Token Usage Trends */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Token Usage Trends (K tokens)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={tokenTrends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip
              formatter={(value: number) => `${value.toFixed(1)}K`}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="input"
              stroke="#3B82F6"
              strokeWidth={2}
              name="Input Tokens"
            />
            <Line
              type="monotone"
              dataKey="output"
              stroke="#10B981"
              strokeWidth={2}
              name="Output Tokens"
            />
            <Line
              type="monotone"
              dataKey="total"
              stroke="#F59E0B"
              strokeWidth={2}
              name="Total Tokens"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Detailed Cost Breakdown Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Detailed Cost Breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requests
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Cost/Req
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  % of Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {llm_costs.by_model.map((item: any) => (
                <tr key={item.model}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.model}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {item.requests.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                    ${item.cost.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    ${(item.cost / item.requests).toFixed(4)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {item.percent.toFixed(1)}%
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  Total
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {totalRequests.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ${totalCost.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ${avgCostPerRequest.toFixed(4)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  100%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
